import { useEffectiveUser } from "@/hooks/useEffectiveUser";
import { useStatsOverview } from "@/hooks/useStatsOverview";
import { LoaderCircle } from "lucide-react";
import { useState, useEffect } from "react";
import CustomLoadingError from "../ui/CustomLoadingError";
import { daysRangeOptions } from "@/lib/options/daysRangeOptions";
import { useAppStore } from "@/store/app";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  XAxis,
  YAxis,
} from "recharts";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";

interface VolumeTrendProps {
  // for URL param: can be undefined, but not null
  exerciseNanoId?: string;
}

export function VolumeTrend({ exerciseNanoId }: VolumeTrendProps) {
  const { user } = useEffectiveUser();
  const selectedRange = useAppStore((s) => s.selectedDaysRange);
  const { data, isLoading, isError } = useStatsOverview(
    Number(selectedRange),
    exerciseNanoId
  );

  // Set chart height based on selected range
  let chartHeight =
    daysRangeOptions.find((option) => option.value === selectedRange)
      ?.chart_height || 250;

  // Show loading state for short time when selected range changes
  const [fakeLoading] = useState(false);

  const barColor = exerciseNanoId
    ? "var(--color-status-icon-caution)"
    : "var(--color-primary)";
  // useEffect(() => {
  //   setFakeLoading(true);
  //   const timer = setTimeout(() => {
  //     setFakeLoading(false);
  //   }, 300);

  //   return () => clearTimeout(timer);
  // }, [selectedRange]);

  if (isError) return <CustomLoadingError />;

  const DATA_KEY = "v";
  const LABEL_KEY = "d";
  const chartConfig = {
    v: {
      label: "Volume(" + (user?.preferred_weight_unit ?? "lb") + ")",
      color: barColor,
    },
    label: {
      color: "var(--text-tx-default)",
    },
  } satisfies ChartConfig;

  if (!data?.volume_trend_data) return;

  if (data?.volume_trend_data.length < 5) {
    chartHeight = 150;
  } else if (data?.volume_trend_data.length < 10) {
    chartHeight = 250;
  } else if (data?.volume_trend_data.length < 30) {
    chartHeight = 400;
  } else if (data?.volume_trend_data.length < 60) {
    chartHeight = 600;
  } else if (data?.volume_trend_data.length < 90) {
    chartHeight = 800;
  }

  return (
    <Card className="w-full mb-10">
      <CardHeader>
        <CardTitle>Volume Trend ({user?.preferred_weight_unit})</CardTitle>
      </CardHeader>
      <CardContent>
        {fakeLoading || isLoading ? (
          <div className="flex flex-col justify-center items-center px-10 text-center min-h-[100px]">
            <p className="text-accent-bg-cold/30">
              <LoaderCircle className="animate-spin w-20 h-20" />
            </p>
          </div>
        ) : (
          <ChartContainer
            config={chartConfig}
            className={" aspect-auto"}
            style={{ height: chartHeight + "px" }}
          >
            <BarChart
              accessibilityLayer
              data={data?.volume_trend_data}
              layout="vertical"
              margin={{
                right: 36,
              }}
            >
              <CartesianGrid horizontal={false} />
              <YAxis
                dataKey={LABEL_KEY}
                type="category"
                tickLine={true}
                tickMargin={10}
                axisLine={false}
                tickFormatter={(value) => value.slice(0, 3)}
                hide
              />
              <XAxis dataKey={DATA_KEY} type="number" hide />
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent indicator="line" />}
              />
              <Bar
                dataKey={DATA_KEY}
                layout="vertical"
                fill={barColor}
                radius={4}
                minPointSize={45}
              >
                <LabelList
                  dataKey={LABEL_KEY}
                  position="insideLeft"
                  offset={8}
                  className="fill-[var(--color-accent-fg-cold)]"
                  fontSize={10}
                />
                <LabelList
                  dataKey={DATA_KEY}
                  position="right"
                  offset={8}
                  className="fill-foreground"
                  fontSize={12}
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
